/*
 * Governance Tile · Phase 5 · "How the Operator is going" column.
 *
 * Renders the third column of the tri-column live scorecard.  Every
 * measured metric binds to /api/admin/governance-tile/summary via
 * useTileData().  Every unmeasured metric renders em dash with a
 * `-pending` testid marker (attestation-honesty rule · anchor GB-013
 * · § V.6).
 *
 * Live mappings:
 *   headline value            → got_wrong (total operator catches)
 *   headline suffix           → ` of ${tasks_total}`
 *   status chip               → three-branch:
 *                                 System quiet (green) when got_wrong = 0
 *                                 System-load · N catches (amber) when > 0
 *                                 em dash (slate) while summary is loading
 *   section 1 total           → got_wrong
 *   section 1 breakdown rows  → all seven pending IJ-034 / IJ-045
 *   inputs · copy-approved    → words_approved
 *   inputs · copy-revoked     → pending
 *   inputs · green-lights     → pending
 *   inputs · red-flags        → pending
 *   inputs · backlog          → pending
 *   signals · lessons-cited   → pending (mockup itself says "counter not wired")
 *   signals · ground-truth    → pending
 *
 * IJ-085 · 2026-07-24 · Phase 5 of the v4-to-v8.8.12 re-cut.
 */
import Row from './Row';
import {
  OP_SCORECARD_TITLE,
  OP_SCORECARD_ICON,
  OP_SUBTITLE,
  OP_STATUS_ZERO,
  OP_STATUS_LOAD_PREFIX,
  OP_HEADLINE_SUB_ACCENT,
  OP_HEADLINE_SUB_REST,
  OP_HEADLINE_SUB_ZERO,
  OP_LEGEND_STRONG,
  OP_LEGEND_BODY,
  OP_CATCHES_LABEL,
  OP_CATCHES_ROWS,
  OP_CATCHES_TOTAL_LABEL,
  OP_INPUTS_LABEL,
  OP_INPUTS_TOOLTIP,
  OP_INPUTS_ROWS,
  OP_SIGNALS_LABEL,
  OP_SIGNALS_ROWS,
} from '../copy/operatorColumn';

const PENDING = '\u2014';
const PENDING_TOOLTIP =
  'Not measured yet. Live counter pending IJ-034 / IJ-045 (operator-input signal service).';

// row.key -> extractor(summary) returning {value, tone} or null.
// null means the endpoint does not emit this signal yet.
const INPUTS_MAP = {
  'green-lights': null,
  'red-flags': null,
  'backlog-directives': null,
  'copy-approved': (s) => ({
    value: String(s.words_approved ?? 0),
    tone: 'green',
  }),
  'copy-revoked': null,
};

const SIGNALS_MAP = {
  'lessons-cited': null,
  // SPD v6.01 · IJ-092 · renamed key IJ-122 (LESSON-024 · counter-name
  // truthfulness).  Prefer the new key; fall back to the old key for
  // one release so a stale frontend cache does not blank the counter.
  'ground-truth-flips': (s) => {
    const raw =
      s.judge_misses_caught_retroactively ?? s.ground_truth_flips_today;
    if (raw === null || raw === undefined) {
      return null;
    }
    return {
      value: String(raw),
      tone: raw > 0 ? 'amber' : 'slate',
    };
  },
};

function SectionHeader({ label, tooltip }) {
  return (
    <div className="flex items-center gap-1 mt-4 mb-2">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
        {label}
      </span>
      {tooltip ? (
        <span
          title={tooltip}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 text-[9px] text-slate-400 cursor-help"
        >
          ?
        </span>
      ) : null}
    </div>
  );
}

function MappedRow({ row, map, summary, testIdPrefix }) {
  const extractor = map[row.key];
  if (extractor === null || extractor === undefined) {
    return (
      <Row
        label={row.label}
        tooltip={
          row.tooltip ? `${row.tooltip} \u00B7 ${PENDING_TOOLTIP}` : PENDING_TOOLTIP
        }
        value={PENDING}
        tone="slate"
        testId={`${testIdPrefix}-${row.key}-pending`}
      />
    );
  }
  if (!summary) {
    return (
      <Row
        label={row.label}
        tooltip={row.tooltip}
        value={PENDING}
        tone="slate"
        testId={`${testIdPrefix}-${row.key}-loading`}
      />
    );
  }
  const r = extractor(summary);
  return (
    <Row
      label={row.label}
      tooltip={row.tooltip}
      value={r.value}
      tone={r.tone}
      testId={`${testIdPrefix}-${row.key}`}
    />
  );
}

export default function OperatorColumn({ summary, loading, error }) {
  const workingSummary = error ? null : summary;
  const gotWrong = workingSummary?.got_wrong ?? null;
  const tasksTotal = workingSummary?.tasks_total ?? null;

  // Live headline value + suffix.
  const headlineValue = gotWrong === null ? PENDING : String(gotWrong);
  const headlineSuffix =
    tasksTotal === null ? ` ${PENDING}` : ` of ${tasksTotal}`;

  // Live status chip.
  let statusChip;
  let statusChipTone;
  if (gotWrong === null) {
    statusChip = PENDING;
    statusChipTone = 'slate';
  } else if (gotWrong === 0) {
    statusChip = OP_STATUS_ZERO;
    statusChipTone = 'green';
  } else {
    statusChip = `${OP_STATUS_LOAD_PREFIX} \u00B7 ${gotWrong} catches`;
    statusChipTone = 'amber';
  }

  // Live sub-line: yellow accent then slate rest.
  let subAccent;
  let subRest;
  if (gotWrong === null) {
    subAccent = PENDING;
    subRest = '';
  } else if (gotWrong === 0) {
    subAccent = OP_HEADLINE_SUB_ZERO;
    subRest = '';
  } else {
    subAccent = OP_HEADLINE_SUB_ACCENT;
    subRest = OP_HEADLINE_SUB_REST;
  }

  return (
    <section
      data-testid="governance-tile-operator-column"
      className="rounded-xl border border-slate-800/70 bg-slate-950/40 p-5"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <span className="text-[16px] text-slate-300" aria-hidden="true">
            {OP_SCORECARD_ICON}
          </span>
          <h3 className="text-[15px] font-semibold text-slate-100">
            {OP_SCORECARD_TITLE}
          </h3>
        </div>
        <span
          data-testid="governance-tile-operator-status-chip"
          className={
            'text-[10px] font-medium px-2 py-0.5 rounded border whitespace-nowrap ' +
            (statusChipTone === 'amber'
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
              : statusChipTone === 'green'
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                : 'border-slate-500/30 bg-slate-500/10 text-slate-300')
          }
        >
          {statusChip}
        </span>
      </div>
      <p className="text-[11px] text-slate-400 leading-snug mb-4">
        {OP_SUBTITLE}
      </p>

      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span
            data-testid="governance-tile-operator-headline-value"
            className="text-[36px] font-bold text-slate-100 tabular-nums leading-none"
          >
            {headlineValue}
          </span>
          <span className="text-[13px] text-slate-400">{headlineSuffix}</span>
        </div>
        <p
          data-testid="governance-tile-operator-headline-sub"
          className="text-[11px] leading-snug mt-1"
        >
          <span className="text-amber-300">{subAccent}</span>
          <span className="text-slate-400">{subRest}</span>
        </p>
      </div>

      <div
        data-testid="governance-tile-operator-legend"
        className="rounded p-2 mb-4 border-l-2 border-amber-500 bg-amber-500/[0.08] text-[11px] text-slate-300 leading-snug"
      >
        <strong className="text-amber-300">{OP_LEGEND_STRONG}</strong>{' '}
        {OP_LEGEND_BODY}
      </div>

      <SectionHeader label={OP_CATCHES_LABEL} />
      <div className="space-y-0">
        {OP_CATCHES_ROWS.map((r) => (
          <Row
            key={r.key}
            label={r.label}
            tooltip={r.tooltip ? `${r.tooltip} \u00B7 ${PENDING_TOOLTIP}` : PENDING_TOOLTIP}
            value={PENDING}
            tone="slate"
            testId={`governance-tile-operator-catch-${r.key}-pending`}
          />
        ))}
        <div
          data-testid={
            gotWrong === null
              ? 'governance-tile-operator-catch-total-loading'
              : 'governance-tile-operator-catch-total'
          }
          className="grid grid-cols-[1fr_auto] gap-x-3 items-baseline border-t border-dashed border-slate-700 mt-2 pt-2"
        >
          <span className="text-[12px] font-semibold text-slate-100 leading-snug">
            {OP_CATCHES_TOTAL_LABEL}
          </span>
          <span
            className={
              'px-2 py-0.5 rounded border text-[11px] font-semibold tabular-nums whitespace-nowrap ' +
              (gotWrong && gotWrong > 0
                ? 'text-amber-300 bg-amber-500/10 border-amber-500/30'
                : 'text-slate-300 bg-slate-500/10 border-slate-500/30')
            }
          >
            {headlineValue}
          </span>
        </div>
      </div>

      <SectionHeader label={OP_INPUTS_LABEL} tooltip={OP_INPUTS_TOOLTIP} />
      <div className="space-y-0">
        {OP_INPUTS_ROWS.map((r) => (
          <MappedRow
            key={r.key}
            row={r}
            map={INPUTS_MAP}
            summary={workingSummary}
            testIdPrefix="governance-tile-operator-input"
          />
        ))}
      </div>

      <SectionHeader label={OP_SIGNALS_LABEL} />
      <div className="space-y-0">
        {OP_SIGNALS_ROWS.map((r) => (
          <MappedRow
            key={r.key}
            row={r}
            map={SIGNALS_MAP}
            summary={workingSummary}
            testIdPrefix="governance-tile-operator-signal"
          />
        ))}
      </div>

      {loading ? (
        <p
          data-testid="governance-tile-operator-loading"
          className="mt-3 text-[10px] italic text-slate-500"
        >
          Live counters loading&hellip;
        </p>
      ) : null}
      {error ? (
        <p
          data-testid="governance-tile-operator-error"
          className="mt-3 text-[10px] text-rose-400"
        >
          Live counter fetch failed. Rows show pending until the endpoint recovers.
        </p>
      ) : null}
    </section>
  );
}
